import React, { useState, useEffect } from 'react';
import {
  Typography,
  Container,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { adminEndpoints } from '../../config/api';

const AdminDashboard = () => {
  const tableColumns = {
    users: ['Username', 'Email', 'Role'],
    'api-keys': ['Name', 'Description', 'Key'],
    'usage-plans': ['Name', 'Quota', 'Throttle'],
  };

  const defaultTableState = Object.keys(tableColumns).reduce((acc, key) => {
    acc[key] = { page: 0, rowsPerPage: 5, search: '', filterField: 'all' };
    return acc;
  }, {});

  const [users, setUsers] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [usagePlans, setUsagePlans] = useState([]);
  const [openDialog, setOpenDialog] = useState({ type: '', action: '', data: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [tableState, setTableState] = useState(defaultTableState);
  const { user, getAccessToken } = useAuth();

  useEffect(() => {
    fetchUsers();
    fetchApiKeys();
    fetchUsagePlans();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = await getAccessToken();

      const response = await fetch(adminEndpoints.users, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setUsers(data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Failed to fetch users');
    }
  };

  const fetchApiKeys = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${adminEndpoints.apiKeys}?user-id=${user.sub}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setApiKeys(data.apiKeys);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      showSnackbar('Failed to fetch API keys');
    }
  };

  const fetchUsagePlans = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${adminEndpoints.usagePlans}?user-id=${user.sub}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setUsagePlans(data.usagePlans);
    } catch (error) {
      console.error('Error fetching usage plans:', error);
      showSnackbar('Failed to fetch usage plans');
    }
  };

  const handleOpenDialog = (type, action, data = null) => {
    setOpenDialog({ type, action, data });
  };

  const handleCloseDialog = () => {
    setOpenDialog({ type: '', action: '', data: null });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const { type, action } = openDialog;
      let response;
      let url = '';
      const token = await getAccessToken();
      let fetchOptions = {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      };
      if (action === 'create') {
        url = `${adminEndpoints[type]}?user-id=${user.sub}`;
        fetchOptions = { ...fetchOptions, method: 'POST', body: JSON.stringify(data) };
      } else if (action === 'edit') {
        url = `${adminEndpoints[type]}/${openDialog.data.id}?user-id=${user.sub}`;
        fetchOptions = { ...fetchOptions, method: 'PUT', body: JSON.stringify(data) };
      }
      response = await fetch(url, fetchOptions);
      if (response.status === 200 || response.status === 201) {
        showSnackbar(`${type} ${action}d successfully`);
        handleCloseDialog();
        if (type === 'users') fetchUsers();
        else if (type === 'api-keys') fetchApiKeys();
        else if (type === 'usage-plans') fetchUsagePlans();
      } else {
        throw new Error('Network response was not ok');
      }
    } catch (error) {
      console.error(`Error ${openDialog.action}ing ${openDialog.type}:`, error);
      showSnackbar(`Failed to ${openDialog.action} ${openDialog.type}`);
    }
  };

  const handleDelete = async (type, id) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${adminEndpoints[type]}/${id}?user-id=${user.sub}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Network response was not ok');
      showSnackbar(`${type} deleted successfully`);
      if (type === 'users') fetchUsers();
      else if (type === 'api-keys') fetchApiKeys();
      else if (type === 'usage-plans') fetchUsagePlans();
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      showSnackbar(`Failed to delete ${type}`);
    }
  };

  const showSnackbar = (message) => {
    setSnackbar({ open: true, message });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSearchChange = (type, value) => {
    setTableState((prev) => ({
      ...prev,
      [type]: { ...prev[type], search: value, page: 0 },
    }));
  };

  const handleFilterChange = (type, value) => {
    setTableState((prev) => ({
      ...prev,
      [type]: { ...prev[type], filterField: value, page: 0 },
    }));
  };

  const handleChangePage = (type, newPage) => {
    setTableState((prev) => ({
      ...prev,
      [type]: { ...prev[type], page: newPage },
    }));
  };

  const handleChangeRowsPerPage = (type, value) => {
    setTableState((prev) => ({
      ...prev,
      [type]: { ...prev[type], rowsPerPage: value, page: 0 },
    }));
  };

  const getFilteredData = (type, data, columns) => {
    const state = tableState[type] || {};
    const searchValue = state.search?.toLowerCase() || '';
    if (!searchValue) return data;

    const fields = state.filterField === 'all'
      ? columns.map((col) => col.toLowerCase())
      : [state.filterField];

    return data.filter((item) =>
      fields.some((field) => String(item[field] ?? '').toLowerCase().includes(searchValue))
    );
  };

  const getPaginatedData = (type, data) => {
    const state = tableState[type] || {};
    const rowsPerPage = state.rowsPerPage || 5;
    const maxPage = Math.max(0, Math.ceil(data.length / rowsPerPage) - 1);
    const page = Math.min(state.page || 0, maxPage);
    const start = page * rowsPerPage;
    return {
      rows: data.slice(start, start + rowsPerPage),
      page,
      rowsPerPage,
    };
  };

  const renderControls = (type, columns) => {
    const state = tableState[type] || {};
    return (
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          label="Search"
          value={state.search || ''}
          onChange={(event) => handleSearchChange(type, event.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id={`filter-label-${type}`}>Filter By</InputLabel>
          <Select
            labelId={`filter-label-${type}`}
            label="Filter By"
            value={state.filterField || 'all'}
            onChange={(event) => handleFilterChange(type, event.target.value)}
          >
            <MenuItem value="all">All Fields</MenuItem>
            {columns.map((column) => (
              <MenuItem key={column} value={column.toLowerCase()}>
                {column}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  const renderTableSection = (type, data, columns) => {
    const filteredData = getFilteredData(type, data, columns);
    const { rows: paginatedData, page, rowsPerPage } = getPaginatedData(type, filteredData);

    return (
      <>
        {renderControls(type, columns)}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column}>{column}</TableCell>
                ))}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((item) => (
                <TableRow key={item.id}>
                  {columns.map((column) => (
                    <TableCell key={`${item.id}-${column}`}>{item[column.toLowerCase()]}</TableCell>
                  ))}
                  <TableCell>
                    <IconButton onClick={() => handleOpenDialog(type, 'edit', item)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(type, item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          rowsPerPageOptions={[5, 10, 25]}
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => handleChangePage(type, newPage)}
          onRowsPerPageChange={(event) => handleChangeRowsPerPage(type, parseInt(event.target.value, 10))}
        />
      </>
    );
  };

  const renderDialog = () => {
    const { type, action, data } = openDialog;
    let fields = [];

    if (type === 'users') {
      fields = ['Username', 'Email', 'Password', 'Role'];
    } else if (type === 'api-keys') {
      fields = ['Name', 'Description'];
    } else if (type === 'usage-plans') {
      fields = ['Name', 'Quota', 'Throttle'];
    }

    return (
      <Dialog open={Boolean(type)} onClose={handleCloseDialog}>
        <DialogTitle>{`${action.charAt(0).toUpperCase() + action.slice(1)} ${type.slice(0, -1)}`}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            {fields.map((field) => (
              <TextField
                key={field}
                margin="normal"
                required
                fullWidth
                label={field}
                name={field.toLowerCase()}
                defaultValue={data ? data[field.toLowerCase()] : ''}
                type={field === 'Password' ? 'password' : 'text'}
              />
            ))}
            <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
              {action.charAt(0).toUpperCase() + action.slice(1)}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ marginTop: 4 }}>
        <Typography variant="h4" gutterBottom>
          Admin Dashboard
        </Typography>
        <Paper elevation={3} sx={{ padding: 3, marginTop: 3 }}>
          <Typography variant="h6" gutterBottom>
            Users
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('users', 'create')}
            sx={{ mb: 2 }}
          >
            Create User
          </Button>
          {renderTableSection('users', users, tableColumns.users)}
        </Paper>
        <Paper elevation={3} sx={{ padding: 3, marginTop: 3 }}>
          <Typography variant="h6" gutterBottom>
            API Keys
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('api-keys', 'create')}
            sx={{ mb: 2 }}
          >
            Create API Key
          </Button>
          {renderTableSection('api-keys', apiKeys, tableColumns['api-keys'])}
        </Paper>
        <Paper elevation={3} sx={{ padding: 3, marginTop: 3 }}>
          <Typography variant="h6" gutterBottom>
            Usage Plans
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('usage-plans', 'create')}
            sx={{ mb: 2 }}
          >
            Create Usage Plan
          </Button>
          {renderTableSection('usage-plans', usagePlans, tableColumns['usage-plans'])}
        </Paper>
      </Box>
      {renderDialog()}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </Container>
  );
};

export default AdminDashboard;
